'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { X, Globe, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { 
  StepperProvider,
  StepperList, 
  StepperItem, 
  StepperTrigger, 
  StepperIndicator, 
  StepperTitle, 
  StepperSeparator,
  StepperContent,
  useStepperContext
} from '../ui/stepper';
import { NewSessionStep1Format } from './NewSessionStep1Format';
import { NewSessionStep2Details } from './NewSessionStep2Details';
import { NewSessionStep2LimitsVariantsCashGame } from './NewSessionStep2LimitsVariantsCashGame';
import { NewSessionStep2SessionsSpinGo } from './NewSessionStep2SessionsSpinGo';
import { NewSessionStep3Summary } from './NewSessionStep3Summary';

export interface SessionFooterConfig {
  primary: { label: string; disabled: boolean; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
}

const SessionFooterContext = React.createContext<{
  setFooter: (config: SessionFooterConfig | null) => void;
} | null>(null);

export const useSessionFooter = () => {
  const ctx = React.useContext(SessionFooterContext);
  return ctx;
};

interface SessionData {
  format?: string;
  universe?: 'Online' | 'Live';
  date?: Date;
  duration?: string;
  tournamentCount?: string;
  buyInTotal?: string;
  buyInLevel?: string;
  stakes?: string;
  customStakes?: string;
  tableType?: string;
  variant?: string;
  customVariant?: string;
  handsCount?: string;
  cashGameVariantLines?: { bankrollId?: string; variant: string; stakes: string; tableType?: string | null; handsCount?: string | number | null; profit?: string | number | null }[] | null;
  bankrolls?: Map<string, {
    bankrollId: string;
    newBalance: string;
    rackback?: string;
    tournamentCount?: string;
    buyInTotal?: string;
    spinGoDetails?: { gameType: string; durationMinutes?: string; buyInLevel?: string; tournamentCount: string; buyInTotal: string; rackback: string; profit?: string }[];
  }>;
  notes?: string;
  sessionProfit?: string;
  sessionProfitCurrency?: string;
  tournamentName?: string;
  mttLiveResultType?: 'ITM' | 'Eliminated';
  mttLiveEntryCount?: string;
  userFestivalId?: string | null;
  pokerFestivalId?: string | null;
  pokerTournamentId?: string | null;
  userTournamentId?: string | null;
  /** Session poker room (Cash Game Live) : champs dédiés */
  cashLiveRoomId?: string;
  cashLiveRoomName?: string;
  cashLiveDurationMinutes?: number | null;
  cashLiveCurrency?: string;
  cashLiveLimits?: string | null;
  cashLiveVariant?: string | null;
  cashLiveBuyIn?: number;
  cashLiveCashout?: number;
  cashLiveNotes?: string | null;
}

interface NewSessionStepperProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editSessionId?: string | null;
  returnTo?: string | null;
  /** Si fourni, ouvre le stepper avec Cash Game / Live et cette bankroll pré-sélectionnée (ex: depuis Cash Live). */
  preSelectedBankrollId?: string | null;
  /** Depuis l'ajout rapide : n'afficher que les formats Online ou Live */
  quickAddMode?: 'online' | 'live';
}

// Composant interne qui utilise le contexte du stepper
const StepperContentWrapper: React.FC<{
  sessionData: SessionData;
  handleStep1Complete: (data: { format: string; universe: 'Online' | 'Live'; date?: Date }) => void;
  handleStep2Complete: (data: Partial<SessionData>) => void;
  handleStep2LimitsVariantsComplete: (data: Partial<SessionData>) => void;
  handleStep3Complete: () => Promise<void>;
  onClose: () => void;
  editSessionId?: string | null;
  returnTo?: string | null;
  quickAddMode?: 'online' | 'live';
  t: any;
}> = ({
  sessionData,
  handleStep1Complete,
  handleStep2Complete,
  handleStep2LimitsVariantsComplete,
  handleStep3Complete,
  onClose,
  editSessionId,
  returnTo,
  quickAddMode,
  t,
}) => {
  const isCashGame = sessionData.format === 'Cash Game';
  const isSpinGo = sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go';
  const isCashGameLive = isCashGame && sessionData.universe === 'Live';
  const hasSessionsStep = isCashGame && !isCashGameLive || isSpinGo;

  return (
    <>
      <StepperList>
        <StepperItem stepId="format">
          <StepperTrigger>
            <StepperIndicator stepId="format" />
            <StepperTitle stepId="format">{t('step1.title')}</StepperTitle>
          </StepperTrigger>
        </StepperItem>
        <StepperSeparator stepId="format" />
        <StepperItem stepId="details">
          <StepperTrigger>
            <StepperIndicator stepId="details" />
            <StepperTitle stepId="details">{t('step2.title')}</StepperTitle>
          </StepperTrigger>
        </StepperItem>
        <StepperSeparator stepId="details" />
        {hasSessionsStep && (
          <>
            <StepperItem stepId="limitsVariants">
              <StepperTrigger>
                <StepperIndicator stepId="limitsVariants" />
                <StepperTitle stepId="limitsVariants">{t('step2Sessions.title')}</StepperTitle>
              </StepperTrigger>
            </StepperItem>
            <StepperSeparator stepId="limitsVariants" />
          </>
        )}
        <StepperItem stepId="summary">
          <StepperTrigger>
            <StepperIndicator stepId="summary" />
            <StepperTitle stepId="summary">{t('step3.title')}</StepperTitle>
          </StepperTrigger>
        </StepperItem>
      </StepperList>

      <StepperContent stepId="format">
        <NewSessionStep1Format 
          onComplete={handleStep1Complete}
          initialData={sessionData}
          onClose={onClose}
          quickAddMode={quickAddMode}
        />
      </StepperContent>

      <StepperContent stepId="details">
        <NewSessionStep2Details 
          onComplete={handleStep2Complete}
          sessionData={sessionData}
          onClose={onClose}
        />
      </StepperContent>

      {isCashGame && (
        <StepperContent stepId="limitsVariants">
          <NewSessionStep2LimitsVariantsCashGame
            sessionData={sessionData}
            onComplete={(data) => {
              handleStep2LimitsVariantsComplete({
                cashGameVariantLines: data.cashGameVariantLines,
                bankrolls: data.bankrolls,
                duration: data.duration,
              });
            }}
          />
        </StepperContent>
      )}
      {isSpinGo && (
        <StepperContent stepId="limitsVariants">
          <NewSessionStep2SessionsSpinGo
            sessionData={sessionData}
            onComplete={(data) => {
              handleStep2LimitsVariantsComplete({
                bankrolls: data.bankrolls,
                duration: data.duration,
              });
            }}
          />
        </StepperContent>
      )}

      <StepperContent stepId="summary">
        <NewSessionStep3Summary 
          sessionData={sessionData}
          editSessionId={editSessionId}
          returnTo={returnTo}
          onComplete={handleStep3Complete}
        />
      </StepperContent>
    </>
  );
};

export const NewSessionStepper: React.FC<NewSessionStepperProps> = ({ 
  isOpen, 
  onClose,
  onSuccess,
  editSessionId,
  returnTo,
  preSelectedBankrollId,
  quickAddMode,
}) => {
  const t = useTranslations('sessions.new.stepper');
  const router = useRouter();
  
  const [sessionData, setSessionData] = useState<SessionData>({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [footerConfig, setFooterConfig] = useState<SessionFooterConfig | null>(null);

  const footerContextValue = useMemo(() => ({ setFooter: setFooterConfig }), []);

  const handleClose = useCallback(() => {
    setSessionData({});
    setCurrentStepIndex(0);
    onClose();
    // Ne pas rediriger lors de la fermeture manuelle, seulement après validation réussie
  }, [onClose]);

  const handleStep1Complete = useCallback((data: { format: string; universe: 'Online' | 'Live'; date?: Date }) => {
    setSessionData(prev => ({ ...prev, ...data }));
  }, []);

  const handleStep2Complete = useCallback((data: Partial<SessionData>) => {
    setSessionData(prev => ({ ...prev, ...data }));
  }, []);

  const handleStep2LimitsVariantsComplete = useCallback((data: Partial<SessionData>) => {
    setSessionData(prev => ({ ...prev, ...data }));
  }, []);

  const handleStep3Complete = async () => {
    // Fermer la modal après validation réussie
    setSessionData({});
    setCurrentStepIndex(0);
    // Appeler onSuccess si fourni (pour recharger la page)
    if (onSuccess) {
      onSuccess();
    } else {
      onClose();
    }
    // Rediriger seulement après validation réussie
    if (returnTo) {
      router.push(returnTo);
    } else {
      router.push('/dashboard');
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Empêcher le scroll du body quand la modal est ouverte
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (isOpen) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, handleClose]);

  // Réinitialiser le stepper quand on ouvre la modal (sans edit) ou charger la session en édition
  useEffect(() => {
    if (!isOpen) return;
    if (editSessionId) {
      let cancelled = false;
      (async () => {
        try {
          const res = await fetch(`/api/sessions/${editSessionId}`);
          if (!res.ok || cancelled) return;
          const data = await res.json();
          const session = data.session;
          if (!session || cancelled) return;
          type BankrollEntry = { bankrollId: string; newBalance: string; rackback?: string; tournamentCount?: string; buyInTotal?: string; spinGoDetails?: { gameType: string; durationMinutes?: string; buyInLevel?: string; tournamentCount: string; buyInTotal: string; rackback: string; profit?: string; cev?: string }[] };
          const bankrollsMap = new Map<string, BankrollEntry>();
          for (const u of session.bankrollUpdates || []) {
            const spinGoDetails = (u.spinGoDetails as { gameType: string; durationMinutes?: number | null; buyInLevel?: number | null; tournamentCount: number; buyInTotal: number | string; rackback?: number | string | null; profit?: number | string | null; cev?: number | string | null }[] | undefined)?.map((d, i) => ({
              gameType: d.gameType ?? `Ligne ${i + 1}`,
              durationMinutes: d.durationMinutes != null ? String(d.durationMinutes) : '',
              buyInLevel: d.buyInLevel != null ? String(d.buyInLevel) : '',
              tournamentCount: String(d.tournamentCount ?? 0),
              buyInTotal: typeof d.buyInTotal === 'string' ? d.buyInTotal : String(d.buyInTotal ?? 0),
              rackback: d.rackback != null ? String(d.rackback) : '',
              profit: d.profit != null ? String(d.profit) : '',
              cev: d.cev != null ? String(d.cev) : '',
            }));
            bankrollsMap.set(u.bankrollId, {
              bankrollId: u.bankrollId,
              newBalance: typeof u.newBalance === 'string' ? u.newBalance : String(u.newBalance),
              ...(session.format === 'Spin & Go' && spinGoDetails?.length ? { spinGoDetails } : {}),
              ...(session.format === 'MTT' && session.universe === 'Online' && u.tournamentCount !== undefined ? { tournamentCount: String(u.tournamentCount), buyInTotal: String(u.buyInTotal ?? 0) } : {}),
            });
          }
          if (!cancelled) {
            setSessionData({
              format: session.format,
              universe: session.universe,
              date: session.date ? new Date(session.date) : undefined,
              duration: session.duration != null ? String(session.duration) : undefined,
              tournamentCount: session.tournamentCount != null ? String(session.tournamentCount) : undefined,
              buyInTotal: session.buyInTotal != null ? String(session.buyInTotal) : undefined,
              buyInLevel: session.buyInLevel != null ? String(session.buyInLevel) : undefined,
              notes: session.notes ?? undefined,
              bankrolls: bankrollsMap,
              stakes: session.stakes ?? undefined,
              variant: session.variant ?? undefined,
              tableType: session.tableType ?? undefined,
              handsCount: session.handsCount != null ? String(session.handsCount) : undefined,
              cashGameVariantLines: session.cashGameVariantLines ?? undefined,
            });
            setCurrentStepIndex(0);
          }
        } catch (e) {
          if (!cancelled) console.error('Error loading session for edit:', e);
        }
      })();
      return () => { cancelled = true; };
    } else if (preSelectedBankrollId) {
      setSessionData({
        format: 'Cash Game',
        universe: 'Live',
        date: new Date(),
        bankrolls: new Map([[preSelectedBankrollId, { bankrollId: preSelectedBankrollId, newBalance: '' }]]),
      });
      setCurrentStepIndex(1); // Aller directement à l’étape 2 (détails / bankroll déjà sélectionnée)
    } else {
      setSessionData({});
      setCurrentStepIndex(0);
    }
  }, [isOpen, editSessionId, preSelectedBankrollId]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop avec flou */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-md z-[100] animate-in fade-in-0"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl my-auto animate-in fade-in-0 zoom-in-95 duration-200 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header fixe */}
          <div className="flex-shrink-0 px-6 pt-6 pb-2 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('title')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('subtitle')}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Zone scrollable : stepper + contenu */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
            <SessionFooterContext.Provider value={footerContextValue}>
              <StepperProvider 
                key={`stepper-${sessionData.format || 'none'}`}
                initialStep={currentStepIndex} 
                onStepChange={setCurrentStepIndex}
                hasConditionalStep={(sessionData.format === 'Cash Game' && sessionData.universe !== 'Live') || sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go'}
              >
                <StepperContentWrapper
                  sessionData={sessionData}
                  handleStep1Complete={handleStep1Complete}
                  handleStep2Complete={handleStep2Complete}
                  handleStep2LimitsVariantsComplete={handleStep2LimitsVariantsComplete}
                  handleStep3Complete={handleStep3Complete}
                  onClose={handleClose}
                  editSessionId={editSessionId}
                  returnTo={returnTo}
                  quickAddMode={quickAddMode}
                  t={t}
                />
              </StepperProvider>
            </SessionFooterContext.Provider>
          </div>

          {/* Footer fixe en bas de la popup */}
          {footerConfig && (
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800 rounded-b-xl flex items-center justify-between gap-4">
              <div>
                {footerConfig.secondary && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={footerConfig.secondary.onClick}
                  >
                    {footerConfig.secondary.label}
                  </Button>
                )}
              </div>
              <Button
                type="button"
                onClick={footerConfig.primary.onClick}
                disabled={footerConfig.primary.disabled}
                className="bg-gradient-to-r from-[#1a2332] via-[#0f172a] to-[#0c1220] hover:opacity-90 text-white font-semibold"
              >
                {footerConfig.primary.label}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
